import { CONFIGURACAO_DNIT_ASFALTO_PADRAO } from "../CadastroCaracterizacaoAsfalto/faixasDnitAsfaltoModel";

export function numeroCbuq(valor) {
  if (valor === "" || valor === null || valor === undefined) return null;
  const texto = String(valor).trim();
  const convertido = Number(texto.includes(",") ? texto.replace(/\./g, "").replace(",", ".") : texto);
  return Number.isFinite(convertido) ? convertido : null;
}

export function mediaCbuq(valores) {
  const validos = valores.map(numeroCbuq).filter((valor) => valor !== null);
  return validos.length ? validos.reduce((soma, valor) => soma + valor, 0) / validos.length : null;
}

export const TABELA_DENSIDADE_AGUA_DNIT = [
  { temperatura: 15.6, densidade: 0.99901 },
  { temperatura: 18.3, densidade: 0.99854 },
  { temperatura: 21.1, densidade: 0.99797 },
  { temperatura: 23.0, densidade: 0.99754 },
  { temperatura: 23.9, densidade: 0.99732 },
  { temperatura: 25.0, densidade: 0.99710 },
  { temperatura: 26.7, densidade: 0.99659 },
  { temperatura: 29.4, densidade: 0.99583 },
];

export function densidadeAguaPorTemperatura(temperatura = 25) {
  const valor = numeroCbuq(temperatura);
  if (valor === null) return null;
  const exato = TABELA_DENSIDADE_AGUA_DNIT.find((item) => item.temperatura === valor);
  if (exato) return exato.densidade;
  const indiceSuperior = TABELA_DENSIDADE_AGUA_DNIT.findIndex((item) => item.temperatura > valor);
  if (indiceSuperior <= 0) return null;
  const inferior = TABELA_DENSIDADE_AGUA_DNIT[indiceSuperior - 1];
  const superior = TABELA_DENSIDADE_AGUA_DNIT[indiceSuperior];
  const proporcao = (valor - inferior.temperatura) / (superior.temperatura - inferior.temperatura);
  return inferior.densidade + ((superior.densidade - inferior.densidade) * proporcao);
}

function calcularCpTracao(cp = {}, constantePrensa = 1) {
  const peso = numeroCbuq(cp.peso);
  const altura = numeroCbuq(cp.altura);
  const diametro = numeroCbuq(cp.diametro);
  const leitura = numeroCbuq(cp.leitura);
  const constante = numeroCbuq(constantePrensa) || 1;
  const forca = leitura !== null ? leitura * constante : null;
  const resistencia = forca !== null && altura && diametro
    ? (2 * forca) / (10 * Math.PI * diametro * altura)
    : null;
  return { ...cp, peso: cp.peso ?? "", altura: cp.altura ?? "", diametro: cp.diametro ?? "", leitura: cp.leitura ?? "", forca, resistencia };
}

export function calcularResistenciaTracao(ensaio = {}, constantePrensa = 1) {
  const cps = Array.from({ length: 3 }, (_, index) => calcularCpTracao(ensaio.cps?.[index] || {}, constantePrensa));
  return { ...ensaio, cps, media: mediaCbuq(cps.map((cp) => cp.resistencia)) };
}

export function calcularDui(ensaio = {}, constantePrensa = 1, limiteMinimo = 0.7) {
  const secos = Array.from({ length: 3 }, (_, index) => calcularCpTracao(ensaio.secos?.[index] || {}, constantePrensa));
  const condicionados = Array.from({ length: 3 }, (_, index) => calcularCpTracao(ensaio.condicionados?.[index] || {}, constantePrensa));
  const rt1 = mediaCbuq(secos.map((cp) => cp.resistencia));
  const rt2 = mediaCbuq(condicionados.map((cp) => cp.resistencia));
  const rrt = rt1 && rt2 !== null ? rt2 / rt1 : null;
  const limite = numeroCbuq(limiteMinimo) ?? 0.7;
  return { ...ensaio, secos, condicionados, rt1, rt2, rrt, conforme: rrt === null ? null : rrt >= limite };
}

export function calcularViscosidadeTemperatura(pontos = []) {
  const validos = pontos.map((ponto) => ({ temperatura: numeroCbuq(ponto.temperatura), viscosidade: numeroCbuq(ponto.viscosidade) }))
    .filter((ponto) => ponto.temperatura !== null && ponto.viscosidade > 0);
  if (validos.length < 2) return { pontos, mistura: null, compactacao: null };
  const mediaTemperatura = mediaCbuq(validos.map((ponto) => ponto.temperatura));
  const mediaLogViscosidade = mediaCbuq(validos.map((ponto) => Math.log(ponto.viscosidade)));
  const denominador = validos.reduce((total, ponto) => total + ((ponto.temperatura - mediaTemperatura) ** 2), 0);
  if (!denominador) return { pontos, mistura: null, compactacao: null };
  const inclinacao = validos.reduce((total, ponto) => total + ((ponto.temperatura - mediaTemperatura) * (Math.log(ponto.viscosidade) - mediaLogViscosidade)), 0) / denominador;
  const intercepto = mediaLogViscosidade - (inclinacao * mediaTemperatura);
  const temperaturaPara = (viscosidade) => inclinacao ? (Math.log(viscosidade) - intercepto) / inclinacao : null;
  const faixa = (viscosidadeMaior, viscosidadeMenor) => {
    const temperaturas = [temperaturaPara(viscosidadeMaior), temperaturaPara(viscosidadeMenor)].filter(Number.isFinite).sort((a, b) => a - b);
    return temperaturas.length === 2 ? { minima: temperaturas[0], maxima: temperaturas[1], media: mediaCbuq(temperaturas) } : null;
  };
  return {
    pontos,
    coeficiente: Math.exp(intercepto),
    base: Math.exp(inclinacao),
    mistura: faixa(95, 75),
    compactacao: faixa(155, 125),
  };
}

export function calcularCaracterizacaoGraudo(determinacoes = [], temperaturaAgua = 25) {
  const densidadeAgua = densidadeAguaPorTemperatura(temperaturaAgua);
  const linhas = determinacoes.map((item) => {
    const cestoReal = numeroCbuq(item.cestoReal ?? item.cesto);
    const imersoComCestoReal = numeroCbuq(item.imersoComCestoReal ?? item.imersoComCesto);
    const cestoAparente = numeroCbuq(item.cestoAparente ?? item.cesto);
    const imersoComCestoAparente = numeroCbuq(item.imersoComCestoAparente ?? item.imersoComCesto);
    const seco = numeroCbuq(item.seco);
    const umido = numeroCbuq(item.umido);
    const imersoLegadoReal = cestoReal !== null && imersoComCestoReal !== null ? imersoComCestoReal - cestoReal : null;
    const imersoLegadoAparente = cestoAparente !== null && imersoComCestoAparente !== null ? imersoComCestoAparente - cestoAparente : null;
    const imerso = numeroCbuq(item.imerso) ?? imersoLegadoReal ?? imersoLegadoAparente;
    const volumeReal = seco !== null && imerso !== null ? seco - imerso : null;
    const volumeAparente = umido !== null && imerso !== null ? umido - imerso : null;
    const densidadeRelativaReal = seco !== null && volumeReal ? seco / volumeReal : null;
    const densidadeRelativaAparente = seco !== null && volumeAparente ? seco / volumeAparente : null;
    return {
      ...item,
      imerso,
      imersoReal: imerso,
      imersoAparente: imerso,
      volumeReal,
      volumeAparente,
      densidadeRelativaReal,
      densidadeRelativaAparente,
      massaEspecificaReal: densidadeRelativaReal !== null && densidadeAgua !== null
        ? densidadeRelativaReal * densidadeAgua
        : null,
      massaEspecificaAparente: densidadeRelativaAparente !== null && densidadeAgua !== null
        ? densidadeRelativaAparente * densidadeAgua
        : null,
      absorcao: seco && umido !== null ? ((umido - seco) / seco) * 100 : null,
    };
  });
  const massaEspecificaReal = mediaCbuq(linhas.map((linha) => linha.massaEspecificaReal));
  const massaEspecificaAparente = mediaCbuq(linhas.map((linha) => linha.massaEspecificaAparente));
  const densidadeRelativaReal = mediaCbuq(linhas.map((linha) => linha.densidadeRelativaReal));
  const densidadeRelativaAparente = mediaCbuq(linhas.map((linha) => linha.densidadeRelativaAparente));
  const absorcao = mediaCbuq(linhas.map((linha) => linha.absorcao));
  const massaEspecificaEfetiva = massaEspecificaReal !== null && massaEspecificaAparente !== null
    ? absorcao > 1
      ? (massaEspecificaReal + (2 * massaEspecificaAparente)) / 3
      : (massaEspecificaReal + massaEspecificaAparente) / 2
    : null;
  return {
    determinacoes: linhas,
    densidadeAgua,
    densidadeRelativaReal,
    densidadeRelativaAparente,
    massaEspecificaReal,
    massaEspecificaAparente,
    absorcao,
    massaEspecificaEfetiva,
  };
}

export function calcularCaracterizacaoMiudo(determinacoes = [], temperaturaAgua = 25) {
  const densidadeAgua = densidadeAguaPorTemperatura(temperaturaAgua);
  const linhas = determinacoes.map((item) => {
    const picnometro = numeroCbuq(item.picnometro);
    const picnometroAmostra = numeroCbuq(item.picnometroAmostra);
    const picnometroAmostraSss = numeroCbuq(item.picnometroAmostraSss);
    const picnometroAmostraAgua = numeroCbuq(item.picnometroAmostraAgua);
    const picnometroAgua = numeroCbuq(item.picnometroAgua);
    const pesoAmostra = numeroCbuq(item.pesoAmostra);
    const volumeRecipiente = numeroCbuq(item.volumeRecipiente);
    const seca = picnometro !== null && picnometroAmostra !== null
      ? picnometroAmostra - picnometro
      : numeroCbuq(item.seca);
    const saturada = picnometro !== null && picnometroAmostraSss !== null
      ? picnometroAmostraSss - picnometro
      : numeroCbuq(item.saturada);
    const denominadorReal = seca !== null && picnometroAgua !== null && picnometroAmostraAgua !== null
      ? seca + picnometroAgua - picnometroAmostraAgua
      : null;
    const denominadorAparente = saturada !== null && picnometroAgua !== null && picnometroAmostraAgua !== null
      ? saturada + picnometroAgua - picnometroAmostraAgua
      : null;
    const densidadeRelativaReal = seca !== null && denominadorReal ? seca / denominadorReal : null;
    const densidadeRelativaAparente = seca !== null && denominadorAparente ? seca / denominadorAparente : null;
    return {
      ...item,
      picnometroAmostra: picnometroAmostra ?? (picnometro !== null && seca !== null ? picnometro + seca : null),
      picnometroAmostraSss: picnometroAmostraSss ?? (picnometro !== null && saturada !== null ? picnometro + saturada : null),
      seca,
      saturada,
      densidadeRelativaReal,
      densidadeRelativaAparente,
      massaEspecifica: densidadeRelativaReal !== null && densidadeAgua !== null
        ? densidadeRelativaReal * densidadeAgua
        : null,
      massaEspecificaReal: densidadeRelativaReal !== null && densidadeAgua !== null
        ? densidadeRelativaReal * densidadeAgua
        : null,
      massaEspecificaAparente: densidadeRelativaAparente !== null && densidadeAgua !== null
        ? densidadeRelativaAparente * densidadeAgua
        : null,
      massaUnitariaSolta: pesoAmostra !== null && volumeRecipiente ? pesoAmostra / volumeRecipiente : null,
      absorcao: seca && saturada !== null ? ((saturada - seca) / seca) * 100 : null,
    };
  });
  const massaEspecificaReal = mediaCbuq(linhas.map((linha) => linha.massaEspecificaReal));
  const massaEspecificaAparente = mediaCbuq(linhas.map((linha) => linha.massaEspecificaAparente));
  return {
    determinacoes: linhas,
    densidadeAgua,
    densidadeRelativaReal: mediaCbuq(linhas.map((linha) => linha.densidadeRelativaReal)),
    densidadeRelativaAparente: mediaCbuq(linhas.map((linha) => linha.densidadeRelativaAparente)),
    massaEspecificaReal,
    massaEspecificaAparente,
    massaUnitariaSolta: mediaCbuq(linhas.map((linha) => linha.massaUnitariaSolta)),
    absorcao: mediaCbuq(linhas.map((linha) => linha.absorcao)),
    massaEspecificaEfetiva: massaEspecificaReal,
  };
}

export function calcularCaracterizacaoFiler(determinacoes = []) {
  const linhas = determinacoes.map((item) => {
    const leituraInicial = numeroCbuq(item.lechatelierInicial);
    const leituraFinal = numeroCbuq(item.lechatelierFinal);
    const pesoMaterial = numeroCbuq(item.pesoMaterial);
    const volumeDeslocado = leituraInicial !== null && leituraFinal !== null
      ? leituraFinal - leituraInicial
      : null;
    return {
      ...item,
      volumeDeslocado,
      densidadeReal: pesoMaterial !== null && volumeDeslocado > 0 ? pesoMaterial / volumeDeslocado : null,
    };
  });
  const densidades = linhas.map((linha) => linha.densidadeReal).filter((valor) => valor !== null);
  const massaEspecificaReal = mediaCbuq(densidades);
  const minimo = densidades.length ? Math.min(...densidades) : null;
  const maximo = densidades.length ? Math.max(...densidades) : null;
  const desvioPadrao = densidades.length > 1
    ? Math.sqrt(densidades.reduce((total, valor) => total + ((valor - massaEspecificaReal) ** 2), 0) / (densidades.length - 1))
    : null;
  return {
    determinacoes: linhas,
    minimo,
    maximo,
    desvioPadrao,
    diferenca: minimo !== null && maximo !== null ? maximo - minimo : null,
    massaEspecificaReal,
    massaEspecificaAparente: massaEspecificaReal,
    massaEspecificaEfetiva: massaEspecificaReal,
    absorcao: 0,
  };
}

const FATORES_VOLUME = [
  [200, 213, 5.56], [215, 225, 5], [226, 237, 4.55], [238, 250, 4.17],
  [251, 264, 3.85], [265, 276, 3.57], [277, 289, 3.33], [290, 301, 3.03],
  [302, 316, 2.78], [317, 328, 2.5], [329, 340, 2.27], [341, 353, 2.08],
  [354, 367, 1.92], [368, 379, 1.79], [380, 392, 1.67], [393, 405, 1.56],
  [406, 420, 1.47], [421, 431, 1.39], [432, 443, 1.32], [444, 456, 1.25],
  [457, 470, 1.19], [471, 482, 1.14], [483, 495, 1.09], [496, 508, 1.04],
  [509, 535, 1], [536, 546, 0.93], [547, 559, 0.89],
  [560, 573, 0.86], [574, 585, 0.83], [586, 598, 0.81], [599, 610, 0.78], [611, 625, 0.76],
];

export function fatorCorrecaoMarshall(volume) {
  const valor = numeroCbuq(volume);
  if (valor === null) return null;
  return FATORES_VOLUME.find(([minimo, maximo]) => valor >= minimo && valor <= maximo)?.[2] ?? 1;
}

function preverTeorPorParametro(alvo, pontos) {
  const validos = pontos.filter((ponto) => Number.isFinite(ponto.x) && Number.isFinite(ponto.y) && ponto.y > 0);
  if (validos.length < 2) return null;
  const mediaX = validos.reduce((s, p) => s + p.x, 0) / validos.length;
  const mediaLogY = validos.reduce((s, p) => s + Math.log(p.y), 0) / validos.length;
  const numerador = validos.reduce((s, p) => s + ((p.x - mediaX) * (Math.log(p.y) - mediaLogY)), 0);
  const denominador = validos.reduce((s, p) => s + ((p.x - mediaX) ** 2), 0);
  if (!denominador) return null;
  const inclinacao = numerador / denominador;
  return Math.exp((mediaLogY - (inclinacao * mediaX)) + (inclinacao * alvo));
}

function interpolarGrupo(teorAlvo, grupos) {
  if (!Number.isFinite(teorAlvo)) return null;
  const ordenados = grupos
    .filter((grupo) => Number.isFinite(numeroCbuq(grupo.teorMistura)))
    .slice()
    .sort((a, b) => a.teorMistura - b.teorMistura);
  if (!ordenados.length) return null;
  const exato = ordenados.find((grupo) => Math.abs(grupo.teorMistura - teorAlvo) < 0.000001);
  if (exato) return { ...exato, teorMistura: teorAlvo, interpolado: false };
  const superior = ordenados.find((grupo) => grupo.teorMistura > teorAlvo);
  const inferior = ordenados.slice().reverse().find((grupo) => grupo.teorMistura < teorAlvo);
  if (!inferior || !superior) return null;
  const proporcao = (teorAlvo - inferior.teorMistura) / (superior.teorMistura - inferior.teorMistura);
  const campos = ["gmm", "densidadeTeorica", "densidadeAparente", "gse", "pba", "pbe", "filerAsfalto", "estabilidade", "fluencia", "vazios", "vam", "vamMinimo", "vcb", "rbv"];
  const interpolado = { teor: teorAlvo, teorMistura: teorAlvo, interpolado: true };
  campos.forEach((campo) => {
    const inicio = numeroCbuq(inferior[campo]);
    const fim = numeroCbuq(superior[campo]);
    interpolado[campo] = inicio !== null && fim !== null ? inicio + ((fim - inicio) * proporcao) : null;
  });
  return interpolado;
}

export const REQUISITOS_DNIT_031_2024 = CONFIGURACAO_DNIT_ASFALTO_PADRAO;

function valorRequisito(configuracao, campo, fallback) {
  return numeroCbuq(configuracao?.[campo]) ?? numeroCbuq(CONFIGURACAO_DNIT_ASFALTO_PADRAO[campo]) ?? fallback;
}

export function vamMinimoDnit(faixaId, volumeVazios, configuracao = CONFIGURACAO_DNIT_ASFALTO_PADRAO) {
  const vazios = numeroCbuq(volumeVazios);
  const faixa = configuracao?.vam?.[faixaId] || CONFIGURACAO_DNIT_ASFALTO_PADRAO.vam?.[faixaId];
  const v3 = numeroCbuq(faixa?.v3);
  const v4 = numeroCbuq(faixa?.v4);
  const v5 = numeroCbuq(faixa?.v5);

  if (vazios === null || v3 === null || v4 === null || v5 === null || vazios < 3 || vazios > 5) return null;
  if (vazios <= 4) return v3 + ((v4 - v3) * (vazios - 3));
  return v4 + ((v5 - v4) * (vazios - 4));
}

function atendeFaixa(valor, [minimo, maximo]) {
  const numero = numeroCbuq(valor);
  return numero === null ? null : numero >= minimo && numero <= maximo;
}

export function calcularMarshall(
  grupos = [],
  densidadeAgregados,
  densidadeLigante,
  constanteMarshall = 1,
  faixaId = "C125",
  densidadeAparenteAgregados = densidadeAgregados,
  configuracaoDnit = CONFIGURACAO_DNIT_ASFALTO_PADRAO,
  percentualFiler = null,
  energiaCompactacao = 75,
) {
  const densAgAparente = numeroCbuq(densidadeAparenteAgregados);
  const densLig = numeroCbuq(densidadeLigante);
  const filer = numeroCbuq(percentualFiler);
  const energia = numeroCbuq(energiaCompactacao);
  const constante = numeroCbuq(constanteMarshall) || 1;
  const vaziosMin = valorRequisito(configuracaoDnit, "vaziosMin", 3);
  const vaziosMax = valorRequisito(configuracaoDnit, "vaziosMax", 5);
  const rbvMin = valorRequisito(configuracaoDnit, "rbvMin", 65);
  const rbvMax = valorRequisito(configuracaoDnit, "rbvMax", 75);
  const estabilidadeMinima = valorRequisito(configuracaoDnit, "estabilidadeMinima", 500);
  const fluenciaMinima = valorRequisito(configuracaoDnit, "fluenciaMinima", 2);
  const fluenciaMaxima = valorRequisito(configuracaoDnit, "fluenciaMaxima", 4.5);
  const filerAsfaltoMin = valorRequisito(configuracaoDnit, "filerAsfaltoMin", 0.6);
  const filerAsfaltoMax = valorRequisito(configuracaoDnit, "filerAsfaltoMax", 1.6);
  const resultados = grupos.map((grupo) => {
    const teor = numeroCbuq(grupo.teor);
    const determinacoesGmm = Array.from({ length: 3 }, (_, index) =>
      numeroCbuq(grupo.gmmDeterminacoes?.[index] ?? (index === 0 ? grupo.gmm : "")));
    const gmmValidos = determinacoesGmm.filter((valor) => valor !== null);
    const gmmMedio = mediaCbuq(gmmValidos);
    const gmmCompleto = determinacoesGmm.every((valor) => valor !== null);
    const cps = (grupo.cps || []).map((cp) => {
      const pesoAr = numeroCbuq(cp.pesoAr);
      const pesoSss = numeroCbuq(cp.pesoSss);
      const pesoImerso = numeroCbuq(cp.pesoImerso);
      const leitura = numeroCbuq(cp.leitura);
      const fluencia = numeroCbuq(cp.fluencia);
      const volume = (pesoSss ?? pesoAr) !== null && pesoImerso !== null ? (pesoSss ?? pesoAr) - pesoImerso : null;
      const densidadeAparente = pesoAr !== null && volume ? pesoAr / volume : null;
      const absorcaoCp = pesoAr && pesoSss !== null ? ((pesoSss - pesoAr) / pesoAr) * 100 : null;
      const fatorCorrecao = fatorCorrecaoMarshall(volume);
      const estabilidadeCorrigida = leitura !== null && fatorCorrecao !== null ? leitura * fatorCorrecao * constante : null;
      return { ...cp, pesoSss: cp.pesoSss ?? "", fluencia: cp.fluencia ?? "", volume, absorcaoCp, densidadeAparente, fatorCorrecao, estabilidadeCorrigida, fluenciaCalculada: fluencia };
    });
    const arredondar = (valor, casas) => Math.round((valor + Number.EPSILON) * (10 ** casas)) / (10 ** casas);
    const teorMistura = teor !== null ? arredondar(teor, 2) : null;
    const percentualAgregados = teorMistura !== null ? arredondar(100 - teorMistura, 2) : null;
    const densidadeTeorica = gmmMedio;
    const gse = percentualAgregados !== null && densidadeTeorica && teorMistura !== null && densLig
      ? percentualAgregados / ((100 / densidadeTeorica) - (teorMistura / densLig))
      : null;
    const pba = gse && densAgAparente && densLig
      ? 100 * ((gse - densAgAparente) / (gse * densAgAparente)) * densLig
      : null;
    const pbe = teorMistura !== null && pba !== null && percentualAgregados !== null
      ? teorMistura - ((pba / 100) * percentualAgregados)
      : null;
    const filerAsfalto = filer !== null && pbe ? filer / pbe : null;
    const cpsComParametros = cps.map((cp) => {
      const gmb = numeroCbuq(cp.densidadeAparente);
      const vaziosCp = gmb !== null && densidadeTeorica ? 100 * (1 - (gmb / densidadeTeorica)) : null;
      const vamCp = gmb !== null && percentualAgregados !== null && densAgAparente
        ? 100 - ((gmb * percentualAgregados) / densAgAparente)
        : null;
      const vcbCp = vamCp !== null && vaziosCp !== null ? vamCp - vaziosCp : null;
      const rbvCp = vcbCp !== null && vamCp ? (vcbCp / vamCp) * 100 : null;
      return { ...cp, vazios: vaziosCp, vam: vamCp, vcb: vcbCp, rbv: rbvCp };
    });
    const densidadeAparenteMedia = mediaCbuq(cpsComParametros.map((cp) => cp.densidadeAparente));
    const densidadeAparente = densidadeAparenteMedia !== null ? arredondar(densidadeAparenteMedia, 3) : null;
    const vaziosMedia = mediaCbuq(cpsComParametros.map((cp) => cp.vazios));
    const vamMedia = mediaCbuq(cpsComParametros.map((cp) => cp.vam));
    const vcbMedia = mediaCbuq(cpsComParametros.map((cp) => cp.vcb));
    const rbvMedia = mediaCbuq(cpsComParametros.map((cp) => cp.rbv));
    const vazios = vaziosMedia !== null ? arredondar(vaziosMedia, 2) : null;
    const vam = vamMedia !== null ? arredondar(vamMedia, 2) : null;
    const vcb = vcbMedia !== null ? arredondar(vcbMedia, 2) : null;
    const rbv = rbvMedia !== null ? arredondar(rbvMedia, 2) : null;
    const estabilidade = mediaCbuq(cpsComParametros.map((cp) => cp.estabilidadeCorrigida));
    const fluencia = mediaCbuq(cpsComParametros.map((cp) => cp.fluenciaCalculada));
    const vamMinimo = vamMinimoDnit(faixaId, vazios, configuracaoDnit);
    const conformeVazios = atendeFaixa(vazios, [vaziosMin, vaziosMax]);
    const conformeRbv = atendeFaixa(rbv, [rbvMin, rbvMax]);
    const conformeVam = vam === null || vamMinimo === null ? null : vam >= vamMinimo;
    const conformeEstabilidade = energia !== 75 || estabilidade === null ? null : estabilidade >= estabilidadeMinima;
    const conformeFluencia = atendeFaixa(fluencia, [fluenciaMinima, fluenciaMaxima]);
    const conformeFilerAsfalto = atendeFaixa(filerAsfalto, [filerAsfaltoMin, filerAsfaltoMax]);
    const conformeGmm = gmmCompleto ? true : null;
    const conformeGmb = cpsComParametros.length >= 3 && cpsComParametros.every((cp) => numeroCbuq(cp.pesoAr) !== null && numeroCbuq(cp.pesoSss) !== null && numeroCbuq(cp.pesoImerso) !== null) ? true : null;
    const verificacoes = [conformeGmm, conformeGmb, conformeVazios, conformeRbv, conformeVam, conformeFilerAsfalto, conformeEstabilidade, conformeFluencia];
    return {
      ...grupo,
      cps: cpsComParametros,
      teor: teor ?? grupo.teor,
      teorMistura,
      gmm: gmmMedio,
      gmmDeterminacoes: Array.from({ length: 3 }, (_, index) => grupo.gmmDeterminacoes?.[index] ?? (index === 0 ? grupo.gmm ?? "" : "")),
      gmmInformado: gmmCompleto,
      densidadeTeorica,
      densidadeAparente,
      gse,
      pba,
      pbe,
      filerAsfalto,
      estabilidade,
      fluencia,
      vazios,
      vam,
      vamMinimo,
      vcb,
      rbv,
      conformeVazios,
      conformeVam,
      conformeRbv,
      conformeEstabilidade,
      conformeFluencia,
      conformeFilerAsfalto,
      conformeGmm,
      conformeGmb,
      conforme: verificacoes.every((item) => item === true),
    };
  });
  const preenchidos = resultados.filter((grupo) =>
    numeroCbuq(grupo.teor) !== null && grupo.conformeGmm === true && grupo.conformeGmb === true);
  const pontosVazios = preenchidos.map((grupo) => ({ x: numeroCbuq(grupo.vazios), y: numeroCbuq(grupo.teorMistura) }));
  const pontosRbv = preenchidos.map((grupo) => ({ x: numeroCbuq(grupo.rbv), y: numeroCbuq(grupo.teorMistura) }));
  const alvoVazios = (vaziosMin + vaziosMax) / 2;
  const alvoRbv = (rbvMin + rbvMax) / 2;
  const teorVaziosMin = preverTeorPorParametro(vaziosMin, pontosVazios);
  const teorVaziosMax = preverTeorPorParametro(vaziosMax, pontosVazios);
  const teorRbvMin = preverTeorPorParametro(rbvMin, pontosRbv);
  const teorRbvMax = preverTeorPorParametro(rbvMax, pontosRbv);
  const teorVazios = mediaCbuq([teorVaziosMin, teorVaziosMax]);
  const teorRbv = mediaCbuq([teorRbvMin, teorRbvMax]);
  const teorIndicado = mediaCbuq([teorVaziosMin, teorVaziosMax, teorRbvMin, teorRbvMax]);
  const grupoIndicado = interpolarGrupo(teorIndicado, resultados);
  return {
    grupos: resultados, teorVaziosMin, teorVaziosMax, teorRbvMin, teorRbvMax,
    teorVazios, teorRbv, teorIndicado, grupoIndicado, alvoVazios, alvoRbv,
  };
}

export function densidadeEfetivaMescla(componentes = [], caracterizacoes = []) {
  const soma = componentes.reduce((total, componente, index) => {
    const percentual = numeroCbuq(componente.percentual);
    const materialId = componente.material?.materialId || componente.material?.id;
    const caracterizacao = caracterizacoes.find((item) => item.materialId === materialId);
    const densidade = numeroCbuq(
      caracterizacao?.resultados?.massaEspecificaReal
      ?? caracterizacao?.resultados?.massaEspecificaEfetiva,
    );
    return percentual !== null && densidade ? total + (percentual / densidade) : total;
  }, 0);
  return soma ? 100 / soma : null;
}

export function densidadeAparenteMescla(componentes = [], caracterizacoes = []) {
  const soma = componentes.reduce((total, componente) => {
    const percentual = numeroCbuq(componente.percentual);
    const materialId = componente.material?.materialId || componente.material?.id;
    const caracterizacao = caracterizacoes.find((item) => item.materialId === materialId);
    const densidade = numeroCbuq(
      caracterizacao?.resultados?.massaEspecificaAparente
      ?? caracterizacao?.resultados?.massaEspecificaReal,
    );
    return percentual !== null && densidade ? total + (percentual / densidade) : total;
  }, 0);
  return soma ? 100 / soma : null;
}

export function calcularFracionamento(mescla = [], totalKg, pesoCpKg, percentuaisBetume = []) {
  const passante = (abertura) => numeroCbuq(mescla.find((linha) => linha.abertura === abertura)?.passante);
  const bandas = [
    { faixa: '3/4" (19 mm) a 1/2" (12,7 mm)', percentual: (passante(19.1) ?? 100) - (passante(12.7) ?? 0) },
    { faixa: '1/2" (12,7 mm) a 3/8" (9,5 mm)', percentual: (passante(12.7) ?? 0) - (passante(9.5) ?? 0) },
    { faixa: '3/8" (9,5 mm) a Nº 4 (4,8 mm)', percentual: (passante(9.5) ?? 0) - (passante(4.8) ?? 0) },
    { faixa: 'Nº 4 (4,8 mm) a Nº 8 (2,36 mm)', percentual: (passante(4.8) ?? 0) - (passante(2.36) ?? 0) },
    { faixa: 'Passante Nº 8', percentual: passante(2.36) ?? 0 },
  ];
  let acumulado = 0;
  const pesoCp = numeroCbuq(pesoCpKg) || 0;
  const total = numeroCbuq(totalKg) || 0;
  return {
    fracoes: bandas.map((banda) => {
      const pesoKg = (total * banda.percentual) / 100;
      const pesoParcial = pesoCp * 1000 * banda.percentual / 100;
      acumulado += pesoParcial;
      return { ...banda, pesoKg, pesoParcial, pesoAcumulado: acumulado };
    }),
    betume: percentuaisBetume.map((percentual) => {
      const teorMistura = numeroCbuq(percentual) || 0;
      return {
        percentual,
        pesoGramas: teorMistura < 100 ? (teorMistura / (100 - teorMistura)) * pesoCp * 1000 : null,
      };
    }),
  };
}
